import { metaFor, type StatusMeta } from "@/lib/adminContent";

/**
 * Whether a content record should be visible on the public site right now.
 * Published records are always live; scheduled records go live automatically
 * once their chosen date/time has passed.
 */
export function isContentLive(
  status: string | null | undefined,
  scheduledFor: string | null | undefined,
): boolean {
  if (status === "published") return true;
  if (status === "scheduled" && scheduledFor) {
    const when = new Date(scheduledFor).getTime();
    return !Number.isNaN(when) && when <= Date.now();
  }
  return false;
}

/** True for a scheduled record whose date is still in the future. */
export function isPendingSchedule(
  status: string | null | undefined,
  scheduledFor: string | null | undefined,
): boolean {
  return status === "scheduled" && !isContentLive(status, scheduledFor);
}

/**
 * Status pill metadata that reflects the real-time state of a scheduled record:
 * a scheduled post whose date has passed reads as "Live".
 */
export function contentStatusMeta(
  base: Record<string, StatusMeta>,
  status: string | null | undefined,
  scheduledFor: string | null | undefined,
): StatusMeta {
  if (status === "scheduled") {
    return isContentLive(status, scheduledFor)
      ? { label: "Live", tone: "primary" }
      : { label: "Scheduled", tone: "secondary" };
  }
  return metaFor(base, status);
}

/** Formats an ISO timestamp into a friendly "12 Oct 2026, 09:00" style label. */
export function formatSchedule(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Converts an ISO timestamp into the local value a <input type="datetime-local"> expects. */
export function toDateTimeLocal(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}